import { useEffect, useState, useCallback, useMemo, useRef } from 'react';
import { Link } from 'react-router-dom';
import { useSearchParams } from 'react-router-dom';
import {
  Search, ShoppingBag, Plus, Minus, ChevronDown, RotateCcw,
  Image as ImageIcon, Loader2, X, Eye, CalendarClock,
} from 'lucide-react';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { useCart } from '@/contexts/CartContext';
import { useAuth } from '@/contexts/AuthContext';
import {
  productsAPI, categoriesAPI, suppliersAPI, mainCategoriesAPI, ordersAPI,
} from '@/lib/api';
import { fileUrl } from '@/lib/api';
import { toast } from '@/components/ui/sonner';
import { ProductDetailModal } from '@/components/ProductDetailPanel';
import { formatWindowDate } from '@/lib/windowDate';
import { formatUnitSize, formatPack } from '@/lib/units';
import { money } from '@/lib/format';

function ProductRow({ product, onSelect }) {
  const { addItem } = useCart();
  const moq = Number(product.moq || 0);
  const qtyFloor = moq > 1 ? moq : 1;
  const [qty, setQty] = useState(qtyFloor);
  const img = fileUrl(product.image_url, 128);
  const upcoming = product.window_state === 'upcoming';
  const orderable = product.orderable !== false && product.window_open !== false && !upcoming;
  const opensOn = formatWindowDate(product.window_open_date);

  const handleAdd = () => {
    if (upcoming) {
      toast.info(opensOn ? `Opens on ${opensOn} — you can order then` : 'This vendor isn’t taking orders yet');
      return;
    }
    if (!orderable) {
      toast.error(`Order window for ${product.supplier_name || 'this supplier'} is closed`);
      return;
    }
    const result = addItem(product, qty);
    if (result?.conflict) return;
    toast.success(`${qty}x ${product.name} added to cart`);
    setQty(qtyFloor);
  };

  return (
    <div
      data-testid={`product-row-${product.id}`}
      className="px-3 sm:px-5 py-3 bg-card border-b border-border/50 last:border-0 hover:bg-muted/30 transition-colors group"
    >
      {/* Row 1: thumbnail + full product name (wraps, never truncated) */}
      <div className="flex items-start gap-3">
        {/* Thumbnail with hover Quick View overlay */}
        <div className="relative h-12 w-12 flex-shrink-0">
          <button
            type="button"
            onClick={() => onSelect(product)}
            data-testid={`product-thumb-btn-${product.id}`}
            aria-label={`Quick view ${product.name}`}
            className="block h-12 w-12 rounded-lg overflow-hidden"
          >
            {img ? (
              <img
                src={img}
                alt={product.name}
                loading="lazy"
                decoding="async"
                width={48}
                height={48}
                className="h-12 w-12 object-cover bg-muted"
                data-testid={`product-thumb-${product.id}`}
              />
            ) : (
              <div className="h-12 w-12 bg-muted flex items-center justify-center">
                <ImageIcon className="h-5 w-5 text-teal/40" strokeWidth={1.5} />
              </div>
            )}
          </button>
          {/* Quick View affordance — appears on row hover (desktop) */}
          <button
            type="button"
            onClick={() => onSelect(product)}
            data-testid={`quick-view-${product.id}`}
            aria-label={`Quick view ${product.name}`}
            className="absolute inset-0 hidden sm:flex items-center justify-center rounded-lg bg-black/55 opacity-0 group-hover:opacity-100 transition-opacity"
          >
            <Eye className="h-4 w-4 text-cream" strokeWidth={2} />
          </button>
        </div>

        {/* Name (clickable to open quick view) — full name, wraps as needed */}
        <button
          type="button"
          onClick={() => onSelect(product)}
          data-testid={`product-select-${product.id}`}
          className="flex-1 min-w-0 text-left"
        >
          <p
            className="font-body text-sm sm:text-base font-semibold text-foreground group-hover:text-teal transition-colors break-words"
            data-testid={`product-name-${product.id}`}
          >
            {product.name}
          </p>
          {product.supplier_name && (
            <p
              className="font-body text-[10px] sm:text-xs uppercase tracking-wider text-gold/70"
              data-testid={`product-supplier-${product.id}`}
            >
              {product.supplier_name}
            </p>
          )}
          {formatPack(product) && (
            <p className="font-body text-xs text-foreground/50" data-testid={`unit-size-${product.id}`}>
              {formatPack(product)}
            </p>
          )}
          <span className="font-body text-xs text-teal/70 inline-flex items-center gap-1">
            <Eye className="h-3 w-3" /> Quick view
          </span>
        </button>
      </div>

      {/* Row 2: price + quantity + add — their own row below the name */}
      <div className="flex flex-wrap items-center gap-3 mt-3 pl-[60px]">
        {/* Price */}
        <p
          className="text-left font-body text-sm sm:text-base font-bold text-foreground flex-shrink-0"
          data-testid={`product-price-${product.id}`}
        >
          R{money(product.price)}
          {Number(product.pack_qty || 1) > 1 && (
            <span className="ml-1.5 font-body text-[9px] font-normal text-foreground/40" data-testid={`price-basis-${product.id}`}>/ pack</span>
          )}
          {moq > 1 && (
            <span className="block font-body text-[10px] font-semibold text-gold" data-testid={`moq-badge-${product.id}`}>
              Min: {moq} {Number(product.pack_qty || 1) > 1 ? 'packs' : 'units'}
            </span>
          )}
        </p>

        {/* Quantity + Add */}
        <div className="flex items-center gap-2 ml-auto justify-end">
          {orderable && (
            <div className="flex items-center border border-border rounded-lg overflow-hidden">
              <button
                onClick={() => setQty(q => Math.max(qtyFloor, q - 1))}
                disabled={qty <= qtyFloor}
                className="px-2 py-1.5 text-foreground/50 hover:text-foreground hover:bg-muted transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
                data-testid={`qty-minus-${product.id}`}
              >
                <Minus className="h-3.5 w-3.5" />
              </button>
              <input
                type="number"
                min={qtyFloor}
                value={qty}
                onChange={e => setQty(Math.max(qtyFloor, parseInt(e.target.value) || qtyFloor))}
                className="w-10 text-center font-body text-sm bg-transparent text-foreground border-x border-border py-1.5 [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none"
                data-testid={`qty-input-${product.id}`}
              />
              <button
                onClick={() => setQty(q => q + 1)}
                className="px-2 py-1.5 text-foreground/50 hover:text-foreground hover:bg-muted transition-colors"
                data-testid={`qty-plus-${product.id}`}
              >
                <Plus className="h-3.5 w-3.5" />
              </button>
            </div>
          )}
          <Button
            onClick={handleAdd}
            disabled={!orderable}
            data-testid={`add-to-cart-${product.id}`}
            title={upcoming && opensOn ? `Opens on ${opensOn}` : undefined}
            className={`rounded-lg font-body text-xs px-4 py-2 gap-1.5 shadow-sm ${
              orderable
                ? 'bg-[#C99A4E] border border-transparent text-charcoal hover:bg-gold-light'
                : upcoming
                ? 'bg-gold/20 text-gold border border-gold/40 hover:bg-gold/30 cursor-not-allowed'
                : 'bg-muted text-foreground/50 cursor-not-allowed'
            }`}
            size="sm"
          >
            {upcoming ? <CalendarClock className="h-3.5 w-3.5" /> : <ShoppingBag className="h-3.5 w-3.5" />}
            {upcoming ? (opensOn ? `Opens ${opensOn}` : 'Soon') : !orderable ? 'Closed' : 'Add'}
          </Button>
        </div>
      </div>
    </div>
  );
}

// Collapsible filter section (collapsed by default to reduce visual weight).
function FilterSection({ title, accentClass = 'text-teal', defaultOpen = false, children }) {
  const [open, setOpen] = useState(defaultOpen);
  return (
    <div className="border-b border-border last:border-0">
      <button
        type="button"
        onClick={() => setOpen(o => !o)}
        data-testid={`filter-toggle-${title.toLowerCase()}`}
        className="w-full flex items-center justify-between py-3"
      >
        <span className={`font-body text-xs uppercase tracking-wider font-semibold ${accentClass}`}>{title}</span>
        <ChevronDown className={`h-4 w-4 text-foreground/40 transition-transform ${open ? 'rotate-180' : ''}`} />
      </button>
      {open && <div className="pb-4 flex flex-wrap gap-2">{children}</div>}
    </div>
  );
}

export default function ShopPage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const { addItem, itemCount, total, items, updateQuantity, removeItem } = useCart();
  const { user } = useAuth();
  const [products, setProducts] = useState([]);
  const [categories, setCategories] = useState([]);
  const [suppliers, setSuppliers] = useState([]);
  const [mainCategories, setMainCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [lastOrderId, setLastOrderId] = useState(null);
  const [reordering, setReordering] = useState(false);

  const activeCategory = searchParams.get('category') || '';
  const activeSupplier = searchParams.get('supplier') || '';
  const activeMain = searchParams.get('main') || '';

  useEffect(() => {
    suppliersAPI.listWithWindows()
      .then(r => setSuppliers((r.data || []).filter(s => s.window_state === 'open' || s.window_state === 'upcoming')))
      .catch(() => {});
    mainCategoriesAPI.list({ available: true }).then(r => setMainCategories(r.data)).catch(() => {});
  }, []);

  // Categories narrow to the selected supplier so non-applicable categories hide.
  useEffect(() => {
    let active = true;
    const params = { available: true };
    if (activeSupplier) params.supplier_id = activeSupplier;
    categoriesAPI.list(params).then(r => { if (active) setCategories(r.data); }).catch(() => {});
    return () => { active = false; };
  }, [activeSupplier]);

  // Fetch most recent order for the Quick Reorder button (logged-in only).
  const loadLastOrder = () => {
    if (!user) return;
    ordersAPI.list()
      .then(r => {
        const orders = r.data || [];
        setLastOrderId(orders.length > 0 ? orders[0].id : null);
      })
      .catch(() => {});
  };
  useEffect(() => { loadLastOrder(); }, [user]);

  const reqSeqRef = useRef(0);
  const loadProducts = useCallback(() => {
    const params = {};
    if (activeMain) params.main_category_id = activeMain;
    if (activeCategory) params.category_id = activeCategory;
    if (activeSupplier) params.supplier_id = activeSupplier;
    if (searchTerm.trim()) params.search = searchTerm.trim();
    // Sequence guard: only the most recent request may update state. Without
    // this, a slower earlier response (e.g. the initial unfiltered load) can
    // resolve after a search request and clobber the filtered results.
    const seq = ++reqSeqRef.current;
    productsAPI.list(params)
      .then(r => {
        if (seq !== reqSeqRef.current) return; // stale — a newer request superseded this
        setProducts(r.data);
        setSelectedProduct(prev => prev ? (r.data.find(p => p.id === prev.id) || null) : null);
      })
      .catch(() => {})
      .finally(() => { if (seq === reqSeqRef.current) setLoading(false); });
  }, [activeMain, activeCategory, activeSupplier, searchTerm]);
  // Debounced so rapid typing fires one request, not one per keystroke.
  useEffect(() => {
    const t = setTimeout(loadProducts, 300);
    return () => clearTimeout(t);
  }, [loadProducts]);

  const byName = (a, b) => (a.name || '').localeCompare(b.name || '', undefined, { sensitivity: 'base' });

  const visibleCategories = (activeMain
    ? categories.filter(c => c.main_category_id === activeMain)
    : categories
  ).slice().sort(byName);
  const visibleSuppliers = activeMain
    ? suppliers.filter(s => s.main_category_id === activeMain)
    : suppliers;
  const openSuppliers = visibleSuppliers.filter(s => s.window_state === 'open').slice().sort(byName);
  const upcomingSuppliers = visibleSuppliers.filter(s => s.window_state === 'upcoming').slice().sort(byName);
  const activeMainName = mainCategories.find(m => m.id === activeMain)?.name || '';

  // Map category id -> category (name + main_category_id) for grouping/labelling.
  const categoryById = useMemo(() => {
    const m = {};
    categories.forEach(c => { m[c.id] = c; });
    return m;
  }, [categories]);
  const mainNameById = useMemo(() => {
    const m = {};
    mainCategories.forEach(mc => { m[mc.id] = mc.name; });
    return m;
  }, [mainCategories]);

  // When no category filter is active, group products by MAIN CATEGORY (A-Z),
  // then by CATEGORY (A-Z) within each main, then products by name (A-Z).
  // Null when a category is selected (list rendered flat + sorted in that case).
  const groupedProducts = useMemo(() => {
    if (activeCategory) return null;
    const localeSort = (a, b) => a.name.localeCompare(b.name, undefined, { sensitivity: 'base' });
    const mains = {};
    products.forEach(p => {
      const cat = categoryById[p.category_id];
      const mid = cat?.main_category_id || '';
      const cid = p.category_id || '';
      if (!mains[mid]) mains[mid] = { id: mid || 'other', name: mainNameById[mid] || 'Other', cats: {} };
      if (!mains[mid].cats[cid]) mains[mid].cats[cid] = { id: cid || 'other', name: cat?.name || 'Other', items: [] };
      mains[mid].cats[cid].items.push(p);
    });
    return Object.values(mains)
      .map(m => ({
        id: m.id,
        name: m.name,
        categories: Object.values(m.cats)
          .map(c => ({ id: c.id, name: c.name, items: c.items.slice().sort(byName) }))
          .sort(localeSort),
      }))
      .sort(localeSort);
  }, [products, activeCategory, categoryById, mainNameById]);

  const handleSupplierClick = (supId) => {
    if (supId === '' || supId === activeSupplier) {
      searchParams.delete('supplier');
    } else {
      searchParams.set('supplier', supId);
    }
    searchParams.delete('category');
    setSearchParams(searchParams);
  };

  const handleCategoryClick = (catId) => {
    if (catId === activeCategory) searchParams.delete('category');
    else searchParams.set('category', catId);
    setSearchParams(searchParams);
  };

  const handleReorder = useCallback(async () => {
    if (!lastOrderId) return;
    setReordering(true);
    try {
      const res = await ordersAPI.reorder(lastOrderId);
      const { available = [], unavailable = [] } = res.data;
      available.forEach(it => addItem({ id: it.product_id, ...it }, it.quantity));
      if (available.length) toast.success(`${available.length} item${available.length > 1 ? 's' : ''} added from your last order`);
      if (unavailable.length) toast.warning(`Some items unavailable: ${unavailable.map(u => u.name).join(', ')}`);
      if (!available.length && !unavailable.length) toast.error('Nothing to reorder');
    } catch {
      toast.error('Reorder failed');
    } finally {
      setReordering(false);
    }
  }, [lastOrderId, addItem]);

  const supplierPill = (active) => `rounded-full text-xs font-body font-semibold px-3 py-1.5 transition-all duration-200 ${
    active ? 'bg-gold text-charcoal hover:bg-gold/90 shadow-md' : 'bg-transparent border-2 border-gold/30 text-foreground/70 hover:bg-gold/10 hover:border-gold/50'
  }`;
  const categoryPill = (active) => `rounded-full text-xs font-body font-semibold px-3 py-1.5 transition-all duration-200 ${
    active ? 'bg-teal text-white hover:bg-teal-dark shadow-md' : 'bg-transparent border-2 border-teal/30 text-teal hover:bg-teal/10 hover:border-teal/50'
  }`;

  return (
    <div data-testid="shop-page" className="min-h-screen bg-background">
      <div className="max-w-7xl mx-auto px-3 sm:px-6 lg:px-8 py-6 sm:py-10">
        {/* Page heading (reduced prominence) */}
        <h1 className="font-heading text-2xl sm:text-3xl font-light text-foreground tracking-tight">
          {activeMainName || 'Products'}
        </h1>

        {/* Primary search (sticky below navbar) */}
        <div
          data-testid="search-bar"
          className="sticky top-24 md:top-[7.5rem] z-30 -mx-3 sm:-mx-6 lg:-mx-8 px-3 sm:px-6 lg:px-8 mt-4 mb-6 py-4 bg-background border-b border-border/60"
        >
          <div className="max-w-[900px] mx-auto relative">
            <Search className="absolute left-4 top-1/2 -translate-y-1/2 h-5 w-5 text-teal/50" />
            <Input
              data-testid="search-input"
              placeholder="Search products, suppliers..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="pl-12 pr-4 py-6 rounded-xl bg-background border-border focus:border-teal focus:ring-2 focus:ring-teal/20 font-body text-base text-foreground placeholder:text-foreground/40"
            />
          </div>
        </div>

        {/* Quick reorder (logged-in users with prior orders) */}
        {lastOrderId && (
          <Button
            variant="ctaSecondary"
            onClick={handleReorder}
            disabled={reordering}
            data-testid="quick-reorder-btn"
            className="mb-5 rounded-full font-body text-xs gap-2"
            size="sm"
          >
            {reordering ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <RotateCcw className="h-3.5 w-3.5" />}
            Reorder Previous Order
          </Button>
        )}

        {/* Main content: filters + product list + order panel */}
        <div className="grid grid-cols-1 min-[992px]:grid-cols-[220px_minmax(0,1fr)_280px] min-[1200px]:grid-cols-[240px_minmax(0,1fr)_320px] gap-6 items-start">
          {/* Left sidebar filters */}
          <aside className="bg-secondary/15 rounded-xl border border-border px-4 self-start min-[992px]:sticky min-[992px]:top-[14rem] min-[992px]:max-h-[calc(100vh-15rem)] min-[992px]:overflow-y-auto" data-testid="shop-filters">
            <FilterSection title="Supplier" accentClass="text-gold">
              <Button data-testid="filter-supplier-all" onClick={() => handleSupplierClick('')} className={supplierPill(!activeSupplier)}>All</Button>

              {openSuppliers.length > 0 && (
                <div className="w-full" data-testid="supplier-group-open">
                  <div className="flex items-center gap-2 mt-4 mb-2">
                    <span className="h-2.5 w-2.5 rounded-full bg-green-400 animate-pulse" />
                    <span className="font-body text-sm font-bold uppercase tracking-wider text-green-400">Open Now</span>
                  </div>
                  <div className="flex flex-wrap gap-2">
                    {openSuppliers.map(sup => (
                      <Button key={sup.id} data-testid={`filter-supplier-${sup.id}`} onClick={() => handleSupplierClick(sup.id)} className={supplierPill(activeSupplier === sup.id)}>
                        {sup.name}
                      </Button>
                    ))}
                  </div>
                </div>
              )}

              {upcomingSuppliers.length > 0 && (
                <div className="w-full" data-testid="supplier-group-upcoming">
                  <div className="flex items-center gap-2 mt-4 mb-2">
                    <span className="h-2.5 w-2.5 rounded-full bg-gold" />
                    <span className="font-body text-sm font-bold uppercase tracking-wider text-gold">Opening Soon</span>
                  </div>
                  <div className="flex flex-wrap gap-2">
                    {upcomingSuppliers.map(sup => (
                      <Button key={sup.id} data-testid={`filter-supplier-${sup.id}`} onClick={() => handleSupplierClick(sup.id)} className={supplierPill(activeSupplier === sup.id)}>
                        {sup.name}
                      </Button>
                    ))}
                  </div>
                </div>
              )}
            </FilterSection>
            <FilterSection title="Category" accentClass="text-teal">
              <Button data-testid="filter-all" onClick={() => { searchParams.delete('category'); setSearchParams(searchParams); }} className={categoryPill(!activeCategory)}>All</Button>
              {visibleCategories.map(cat => (
                <Button key={cat.id} data-testid={`filter-${cat.slug}`} onClick={() => handleCategoryClick(cat.id)} className={categoryPill(activeCategory === cat.id)}>
                  {cat.name}
                </Button>
              ))}
            </FilterSection>
          </aside>

          {/* Product area */}
          <div className="min-w-0">
            {loading ? (
              <div className="space-y-3">
                {[...Array(6)].map((_, i) => (
                  <div key={i} className="animate-pulse flex items-center gap-4 p-4 bg-card rounded-xl">
                    <div className="h-12 w-12 bg-muted rounded-lg" />
                    <div className="flex-1 h-5 bg-muted rounded w-32 sm:w-48" />
                    <div className="h-5 bg-muted rounded w-16" />
                    <div className="h-8 bg-muted rounded w-24 hidden sm:block" />
                  </div>
                ))}
              </div>
            ) : products.length === 0 ? (
              <div className="text-center py-16 sm:py-24 bg-secondary/15 rounded-2xl" data-testid="no-products">
                <div className="w-12 h-12 sm:w-16 sm:h-16 bg-muted rounded-full flex items-center justify-center mx-auto mb-4">
                  <Search className="h-5 w-5 sm:h-7 sm:w-7 text-teal/50" />
                </div>
                <p className="font-heading text-xl sm:text-2xl text-foreground/60">No products found</p>
                <p className="font-body text-xs sm:text-sm text-foreground/40 mt-2">Try adjusting your search or filters</p>
              </div>
            ) : (
              <>
                {/* Product count */}
                <p className="font-body text-xs sm:text-sm text-foreground/50 mb-3" data-testid="product-count">
                  Showing <span className="font-semibold text-teal">{products.length}</span> products
                </p>

                {activeCategory ? (
                  /* Single category selected — flat list sorted A-Z */
                  <>
                    <div className="hidden sm:flex items-center gap-3 px-5 py-3 bg-muted/50 rounded-t-xl border border-border text-foreground/50">
                      <div className="flex-1 font-body text-xs uppercase tracking-wider font-semibold pl-[60px]">Product</div>
                    </div>
                    <div className="border border-border rounded-xl sm:rounded-t-none sm:border-t-0 overflow-hidden" data-testid="products-list">
                      {products.slice().sort(byName).map(product => (
                        <ProductRow key={product.id} product={product} onSelect={setSelectedProduct} />
                      ))}
                    </div>
                  </>
                ) : (
                  /* No category filter — group by MAIN CATEGORY (A-Z) → CATEGORY (A-Z) → products (A-Z) */
                  <div className="space-y-8" data-testid="products-list">
                    {groupedProducts.map(main => (
                      <div key={main.id} data-testid={`product-main-group-${main.id}`}>
                        <div className="flex items-center gap-2 mb-3 pb-2 border-b border-gold/30">
                          <h2 className="font-heading text-lg font-semibold text-gold" data-testid={`main-group-title-${main.id}`}>
                            {main.name}
                          </h2>
                        </div>
                        <div className="space-y-6">
                          {main.categories.map(group => (
                            <div key={group.id} data-testid={`product-category-group-${group.id}`}>
                              <div className="flex items-center gap-2 mb-2">
                                <span className="h-4 w-1 rounded-full bg-teal flex-shrink-0" />
                                <h3 className="font-body text-sm font-bold uppercase tracking-wider text-teal" data-testid={`category-group-title-${group.id}`}>
                                  {group.name}
                                </h3>
                                <span className="font-body text-xs text-foreground/40">({group.items.length})</span>
                              </div>
                              <div className="border border-border rounded-xl overflow-hidden">
                                {group.items.map(product => (
                                  <ProductRow key={product.id} product={product} onSelect={setSelectedProduct} />
                                ))}
                              </div>
                            </div>
                          ))}
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </>
            )}
          </div>

          {/* Order panel (third column) — detailed editable cart */}
          <aside
            data-testid="order-panel"
            className="bg-card border border-border rounded-2xl self-start flex flex-col overflow-hidden min-[992px]:sticky min-[992px]:top-[14rem] min-[992px]:max-h-[calc(100vh-15rem)]"
          >
            {/* Header */}
            <div className="px-5 py-4 border-b border-border flex items-center justify-between flex-shrink-0">
              <div className="flex items-center gap-2">
                <ShoppingBag className="h-5 w-5 text-teal" strokeWidth={2} />
                <h2 className="font-body text-sm font-bold uppercase tracking-[0.15em] text-foreground">Your Order</h2>
              </div>
              <span className="font-body text-xs text-foreground/50" data-testid="order-panel-count">
                {itemCount} item{itemCount !== 1 ? 's' : ''}
              </span>
            </div>

            {itemCount === 0 ? (
              <div className="px-5 py-6">
                <div className="flex items-baseline justify-between mb-3">
                  <span className="font-body text-sm text-foreground/60">0 Items</span>
                  <span className="font-heading text-2xl font-semibold text-gold" data-testid="order-panel-total">R0.00</span>
                </div>
                <p className="font-body text-sm text-foreground/50 leading-relaxed" data-testid="order-panel-empty">
                  Your order is empty. Browse products and add items to begin.
                </p>
              </div>
            ) : (
              <>
                {/* Items (scrollable) */}
                <div className="flex-1 min-h-0 overflow-y-auto px-5 py-4 space-y-3" data-testid="order-panel-items">
                  {items.map(item => (
                    <div
                      key={item.product_id}
                      data-testid={`order-panel-item-${item.product_id}`}
                      className="pb-3 border-b border-border/50 last:border-0"
                    >
                      <div className="flex items-start justify-between gap-2 mb-2">
                        <div className="flex-1 min-w-0">
                          <p className="font-body text-sm font-semibold text-foreground truncate">{item.name}</p>
                          {item.supplier_name && (
                            <p className="font-body text-[10px] uppercase tracking-wider text-gold/70 mt-0.5 truncate">
                              {item.supplier_name}
                            </p>
                          )}
                          {formatPack(item) && (
                            <p className="font-body text-[11px] text-foreground/50 mt-0.5 truncate" data-testid={`order-panel-size-${item.product_id}`}>
                              {formatPack(item)}
                            </p>
                          )}
                        </div>
                        <button
                          onClick={() => removeItem(item.product_id)}
                          data-testid={`order-panel-remove-${item.product_id}`}
                          aria-label="Remove item"
                          className="text-foreground/40 hover:text-red-400 transition-colors flex-shrink-0"
                        >
                          <X className="h-4 w-4" strokeWidth={2} />
                        </button>
                      </div>
                      <div className="flex items-center justify-between gap-2">
                        <div className="flex items-center border border-border rounded-lg overflow-hidden">
                          <button
                            onClick={() => updateQuantity(item.product_id, item.quantity - 1)}
                            data-testid={`order-panel-minus-${item.product_id}`}
                            aria-label="Decrease quantity"
                            className="px-2 py-1 text-foreground/60 hover:text-foreground hover:bg-muted transition-colors"
                          >
                            <Minus className="h-3.5 w-3.5" strokeWidth={2} />
                          </button>
                          <span className="px-3 py-1 font-body text-sm text-foreground min-w-[2.25rem] text-center" data-testid={`order-panel-qty-${item.product_id}`}>
                            {item.quantity}
                          </span>
                          <button
                            onClick={() => updateQuantity(item.product_id, item.quantity + 1)}
                            data-testid={`order-panel-plus-${item.product_id}`}
                            aria-label="Increase quantity"
                            className="px-2 py-1 text-foreground/60 hover:text-foreground hover:bg-muted transition-colors"
                          >
                            <Plus className="h-3.5 w-3.5" strokeWidth={2} />
                          </button>
                        </div>
                        <p className="font-body text-sm font-bold text-foreground" data-testid={`order-panel-line-${item.product_id}`}>
                          R{money((item.price * item.quantity))}
                        </p>
                      </div>
                    </div>
                  ))}
                </div>

                {/* Footer */}
                <div className="border-t border-border px-5 py-4 space-y-3 flex-shrink-0 bg-card">
                  <div className="flex items-center justify-between">
                    <span className="font-body text-xs uppercase tracking-wider text-foreground/60">Subtotal</span>
                    <span className="font-heading text-2xl font-semibold text-gold" data-testid="order-panel-total">
                      R{money(total)}
                    </span>
                  </div>
                  <Link to="/cart" data-testid="order-panel-review-btn">
                    <Button variant="ctaPrimary" className="w-full rounded-full font-body text-sm font-semibold py-5">
                      Review Order
                    </Button>
                  </Link>
                  <Link to="/checkout" data-testid="order-panel-submit-btn">
                    <Button variant="ctaSecondary" className="w-full rounded-full font-body text-sm font-semibold py-5">
                      Submit Order
                    </Button>
                  </Link>
                </div>
              </>
            )}
          </aside>
        </div>
      </div>

      {/* Product quick-view modal (overlay) */}
      <ProductDetailModal product={selectedProduct} onClose={() => setSelectedProduct(null)} />
    </div>
  );
}
